import { useAuth } from "@clerk/nextjs";
import posthog from "posthog-js";
import { useCallback } from "react";

import { useWizardMode } from "./WizardModeContext";
import { useOnboarding } from "~/hooks";

/**
 * Returns a `track` function that captures an `onboarding:*` PostHog event with
 * the wizard's cross-cutting context (`mode`, `is_authed`, `learning_path`,
 * `step`, `preset`) merged in, so every onboarding event is consistently sliceable. Must
 * be used inside the `WizardModeProvider` (i.e. within `<OnboardingWizard>`).
 *
 * @returns {{ track: (event: string, props?: Record<string, unknown>) => void }}
 */
const useOnboardingTracking = () => {
	const { mode, preset } = useWizardMode();
	const { isSignedIn } = useAuth();
	const { step, learningPath } = useOnboarding();

	const track = useCallback(
		(event: string, props?: Record<string, unknown>) => {
			posthog.capture(event, {
				mode,
				is_authed: !!isSignedIn,
				learning_path: learningPath,
				step,
				preset: preset ? "cpt_study_plan" : null,
				...props,
			});
		},
		[mode, isSignedIn, learningPath, step, preset],
	);

	return { track };
};

export default useOnboardingTracking;
